import { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Alert, Badge, Table } from 'reactstrap';
import { Card, CardBody } from '@g17eco/atoms/card';
import { Dashboard } from '@g17eco/ui-modern/components/ui/dashboard';
import { Loader } from '@g17eco/atoms/loader';
import { generateUrl } from '@routes/util';
import { emissionsTrackerRoutes } from '../routes';
import { useGetImportsQuery, useLazyGetImportDownloadUrlQuery, useUndoImportMutation } from '../api/emissions';
import { BulkImportStatus, BulkImportType } from '../types/emissions';
import { ASSET_TYPE_LABELS } from '@g17eco/emissions-config';
import { Button } from '@g17eco/ui-modern/components/ui/button';

const STATUS_BADGE_COLORS: Record<BulkImportStatus, string> = {
  [BulkImportStatus.Completed]: 'success',
  [BulkImportStatus.PartialFailure]: 'warning',
  [BulkImportStatus.Failed]: 'danger',
  [BulkImportStatus.Processing]: 'info',
};

const STATUS_LABELS: Record<BulkImportStatus, string> = {
  [BulkImportStatus.Completed]: 'Completed',
  [BulkImportStatus.PartialFailure]: 'Partial',
  [BulkImportStatus.Failed]: 'Failed',
  [BulkImportStatus.Processing]: 'Processing',
};

const PAGE_SIZE = 20;

export const EmissionsImportHistoryPage = () => {
  const history = useHistory();
  const { initiativeId = '' } = useParams<{ initiativeId: string }>();
  const [offset, setOffset] = useState(0);
  const [undoingId, setUndoingId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { data, isLoading, isError } = useGetImportsQuery({ initiativeId, limit: PAGE_SIZE, offset });
  const [fetchDownloadUrl] = useLazyGetImportDownloadUrlQuery();
  const [undoImport] = useUndoImportMutation();

  const imports = data?.data ?? [];
  const total = data?.pagination?.total ?? 0;

  const handleBack = () => {
    history.push(generateUrl(emissionsTrackerRoutes.EMISSIONS_ADMIN, { initiativeId }));
  };

  const handleViewActivities = (importId: string) => {
    const pathname = generateUrl(emissionsTrackerRoutes.EMISSIONS_ACTIVITIES, { initiativeId });
    history.push({ pathname, search: `?bulkImportIds=${importId}` });
  };

  const handleDownload = async (importId: string) => {
    setErrorMessage(null);
    const result = await fetchDownloadUrl({ initiativeId, importId });
    if (result.error) {
      setErrorMessage('Failed to download file. The file may no longer be available.');
      return;
    }
    if (result.data?.downloadUrl) {
      window.open(result.data.downloadUrl, '_blank');
    } else {
      setErrorMessage('Download URL is not available. Please try again later.');
    }
  };

  const handleUndo = async (importId: string) => {
    const imp = imports.find((i) => i._id === importId);
    const isAssetImport = imp?.importType === BulkImportType.Asset;
    const message = isAssetImport
      ? 'Are you sure you want to undo this import? All assets and activities created by this import will be deleted.'
      : 'Are you sure you want to undo this import? All activities created by this import will be deleted.';
    if (!window.confirm(message)) {
      return;
    }

    setErrorMessage(null);
    setUndoingId(importId);
    try {
      await undoImport({ initiativeId, importId }).unwrap();
    } catch (error) {
      const message = (error as { data?: { message?: string } })?.data?.message
        || 'Failed to undo import. Please try again.';
      setErrorMessage(message);
    } finally {
      setUndoingId(null);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Dashboard>
      <div className='d-flex align-items-center gap-3 mb-4'>
        <Button variant='ghost' size='icon' onClick={handleBack}>
          <i className='fa fa-arrow-left' />
        </Button>
        <div>
          <h4 className='mb-1 fw-semibold'>Import History</h4>
          <p className='text-ThemeTextLight mb-0'>View past added activities, assets, and bulk imports. Download original files, and undo imports</p>
        </div>
      </div>

      {errorMessage && (
        <Alert color='danger' className='mb-3' toggle={() => setErrorMessage(null)}>
          <i className='fa fa-exclamation-circle me-2' />
          {errorMessage}
        </Alert>
      )}

      <Card className='border-0 shadow-sm'>
        <CardBody className='p-0'>
          {isLoading && <div className='p-4'><Loader /></div>}

          {!isLoading && isError && (
            <div className='p-4'>
              <Alert color='danger' className='mb-0'>
                <i className='fa fa-exclamation-circle me-2' />
                Failed to load import history. Please refresh the page or try again later.
              </Alert>
            </div>
          )}

          {!isLoading && !isError && imports.length === 0 && (
            <div className='p-4 text-center text-ThemeTextLight'>
              <i className='fa fa-inbox fa-2x mb-3 d-block' />
              No imports found. Use Bulk Import to upload activity data.
            </div>
          )}

          {!isLoading && imports.length > 0 && (
            <>
              <Table responsive hover className='mb-0'>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Group</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th className='text-end'>Rows</th>
                    <th className='text-end'>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {imports.map((imp) => (
                    <tr key={imp._id}>
                      <td>{formatDate(imp.created)}</td>
                      <td className='text-capitalize'>
                        {imp.importType === BulkImportType.Asset
                          ? 'Asset'
                          : 'Activity'}
                      </td>
                      <td className='text-capitalize'>
                        {imp.importType === BulkImportType.Asset
                          ? `${ASSET_TYPE_LABELS[imp.assetType as keyof typeof ASSET_TYPE_LABELS] ?? imp.assetType ?? 'Unknown'}`
                          : imp.scope?.replace('scope', 'Scope ') ?? '—'}
                      </td>
                      <td>
                        <Badge color={STATUS_BADGE_COLORS[imp.status]}>
                          {STATUS_LABELS[imp.status]}
                        </Badge>
                      </td>
                      <td className='text-end'>
                        <span className='text-success'>{imp.successfulRows}</span>
                        {imp.failedRows > 0 && (
                          <span className='text-danger ms-1'>/ {imp.failedRows} failed</span>
                        )}
                        <span className='text-ThemeTextLight ms-1'>of {imp.totalRows}</span>
                      </td>
                      <td className='text-end'>
                        {imp.fileId && (
                          <Button
                            variant='outline'
                            size='icon-sm'
                            className='me-1'
                            title='Download original file'
                            onClick={() => handleDownload(imp._id)}
                          >
                            <i className='fa fa-download' />
                          </Button>
                        )}
                        {imp.successfulRows > 0 && (
                          <Button
                            variant='outline'
                            size='icon-sm'
                            className='me-1'
                            title='View activities from this import'
                            onClick={() => handleViewActivities(imp._id)}
                          >
                            <i className='fa fa-list' />
                          </Button>
                        )}
                        <Button
                          variant='outline'
                          size='icon-sm'
                          className='text-danger'
                          title='Undo import'
                          disabled={undoingId === imp._id}
                          onClick={() => handleUndo(imp._id)}
                        >
                          <i className={`fa ${undoingId === imp._id ? 'fa-spinner fa-spin' : 'fal fa-trash'}`} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {total > PAGE_SIZE && (
                <div className='d-flex justify-content-between align-items-center p-3 border-top'>
                  <span className='text-ThemeTextLight'>
                    Showing {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
                  </span>
                  <div className='d-flex gap-2'>
                    <Button
                      color='outline-secondary'
                      size='sm'
                      disabled={offset === 0}
                      onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                    >
                      Previous
                    </Button>
                    <Button
                      color='outline-secondary'
                      size='sm'
                      disabled={offset + PAGE_SIZE >= total}
                      onClick={() => setOffset(offset + PAGE_SIZE)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardBody>
      </Card>
    </Dashboard>
  );
};
